/**
 * Lead date fields are inconsistently stored:
 * - ISO: 2026-09-25T05:50:59.000Z
 * - DD-MM-YYYY: 21-12-2024
 * - DD-MM-YYYYTHH:mm:ss
 * - DD/MM/YYYY
 * Native Date() mis-parses DD-MM-YYYY (Invalid Date or wrong month).
 *
 * Arrival time = when the lead came into CRM (createdAt / date).
 * Never use assignedAt for Fresh / relative age — same for admin & team.
 */

const IST = "Asia/Kolkata";

export function parseLeadDate(value: unknown): Date | null {
  if (value == null || value === "" || value === "NA") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Ignore non-date objects (e.g. leftover BSON wrappers)
  if (typeof value === "object") {
    const anyVal = value as { $date?: unknown; toISOString?: () => string };
    if (typeof anyVal.toISOString === "function") {
      try {
        const d = new Date(anyVal.toISOString());
        return Number.isNaN(d.getTime()) ? null : d;
      } catch {
        return null;
      }
    }
    if (anyVal.$date != null) return parseLeadDate(anyVal.$date);
    return null;
  }

  const raw = String(value).trim();
  if (!raw || raw === "Invalid Date" || raw === "[object Object]") return null;

  // DD-MM-YYYY or DD-MM-YYYYTHH:mm[:ss][.sss][Z]
  const dmY = /^(\d{2})-(\d{2})-(\d{4})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?Z?)?$/i.exec(
    raw
  );
  if (dmY) {
    const day = Number(dmY[1]);
    const month = Number(dmY[2]) - 1;
    const year = Number(dmY[3]);
    const hh = Number(dmY[4] || 0);
    const mm = Number(dmY[5] || 0);
    const ss = Number(dmY[6] || 0);
    const d = new Date(year, month, day, hh, mm, ss);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // DD/MM/YYYY
  const slash = /^(\d{2})\/(\d{2})\/(\d{4})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(
    raw
  );
  if (slash) {
    const d = new Date(
      Number(slash[3]),
      Number(slash[2]) - 1,
      Number(slash[1]),
      Number(slash[4] || 0),
      Number(slash[5] || 0),
      Number(slash[6] || 0)
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const iso = new Date(raw);
  return Number.isNaN(iso.getTime()) ? null : iso;
}

/** Prefer primary, then fallbacks (e.g. date → createdAt). */
export function resolveLeadDate(
  ...candidates: unknown[]
): Date | null {
  for (const c of candidates) {
    const d = parseLeadDate(c);
    if (d) return d;
  }
  return null;
}

function objectIdArrival(row: any): Date | null {
  const id = String(row?._id || row?.leadId || "").trim();
  if (!/^[a-fA-F0-9]{24}$/.test(id)) return null;
  const d = new Date(parseInt(id.slice(0, 8), 16) * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** When the lead arrived in CRM — same for every user, never assignment time. */
export function leadArrivalTime(row: any): Date | null {
  const fromId = objectIdArrival(row);
  const fromFields = resolveLeadDate(
    row?.createdAt,
    row?.date,
    row?.lead_date,
    row?.create_date
  );
  if (fromId && fromFields) {
    // Mapping/process-start createdAt can differ per assignee. Trust insert id
    // when it disagrees by more than 12 hours.
    if (Math.abs(fromId.getTime() - fromFields.getTime()) > 12 * 60 * 60 * 1000) {
      return fromId;
    }
    return fromFields;
  }
  return fromFields || fromId;
}

export function formatLeadDateParts(
  value: unknown,
  fallbacks: unknown[] = []
): { date: string; time: string } | null {
  const d = resolveLeadDate(value, ...fallbacks);
  if (!d) return null;
  const date = d
    .toLocaleDateString("en-GB", { timeZone: IST })
    .replace(/\//g, "-");
  const time = d.toLocaleTimeString("en-IN", {
    timeZone: IST,
    hour: "2-digit",
    minute: "2-digit",
  });
  return { date, time };
}

/** YYYY-MM-DD in Asia/Kolkata */
export function istDateKey(d: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Fresh = Created Date (canonical arrival) is today IST. */
export function isFreshLeadToday(row: any, now: Date = new Date()): boolean {
  const arrival = leadArrivalTime(row);
  return arrival != null && istDateKey(arrival) === istDateKey(now);
}

/**
 * Relative age from lead arrival time.
 * Just now | 1 min ago | 5 min ago | 1h ago | 3h ago | 1d ago …
 */
export function relativeLeadAgeLabel(
  dateTime: unknown,
  nowMs: number = Date.now()
): string | null {
  const d = parseLeadDate(dateTime);
  if (!d) return null;
  const diff = Math.max(0, nowMs - d.getTime());
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return null;
}

/** Newest CRM leads first (arrival / createdAt). Never use updatedAt — a stage
 * change must not jump the lead to the top of the calling list. */
export function compareLeadsNewestFirst(a: any, b: any): number {
  const ts = (row: any) => leadArrivalTime(row)?.getTime() || 0;
  const tb = ts(b);
  const ta = ts(a);
  if (tb !== ta) return tb - ta;
  return String(b?._id || "").localeCompare(String(a?._id || ""));
}

/** Update stage on a list row without changing its position. */
export function patchLeadStageOnRow(
  row: any,
  stage: string,
  userId?: string,
  extras?: { AssignToUser?: any[] }
): any {
  if (!row) return row;
  const next = { ...row, stage };
  const nextAssignees = extras?.AssignToUser;
  if (Array.isArray(nextAssignees)) {
    const kept = nextAssignees.map((id) => String(id || "").trim()).filter(
      Boolean
    );
    const existing = Array.isArray(row.AssignToUser) ? row.AssignToUser : [];
    const filtered = existing.filter((item: any) => {
      const sid = String(
        item?._id || item?.userId || item?.user_id || item?.id || item || ""
      ).trim();
      return kept.includes(sid);
    });
    next.AssignToUser = filtered.length ? filtered : kept;
    const stages = row.AssignToUserStage;
    if (Array.isArray(stages)) {
      next.AssignToUserStage = stages.filter((item: any) => {
        const sid = String(item?.user_id || item?.userId || item || "").trim();
        return !sid || kept.includes(sid);
      });
    }
  }
  const stages = next.AssignToUserStage;
  if (Array.isArray(stages)) {
    const uid = String(userId || "");
    next.AssignToUserStage = stages.map((item: any) => {
      if (!item || typeof item !== "object") return item;
      const sid = String(item.user_id || item.userId || "");
      if (uid && sid && sid !== uid) return item;
      return { ...item, stage };
    });
  }
  return next;
}
